/*
  Error monitoring and session recording, set up the same way as the portal (src/instrument.tsx):
  Sentry with the router integration and replays, and Microsoft Clarity. One difference: Clarity
  only starts after the parent says yes (ConsentBanner), because the app is used around children.
*/
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { clarity } from "react-microsoft-clarity";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { getConsent } from "app/components/ConsentBanner";

const sentryIgnoreList = ["Invalid identifier or password", "Invalid token", "Session expired", "Load failed", "Failed to fetch"];

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({ useEffect, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes }),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    environment: process.env.REACT_APP_NODE_ENV ?? "development",
    release: process.env.REACT_APP_VERSION || undefined,
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACE_RATE || "0.2"),
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const messages: string[] = [];
      if (error && typeof error === "object" && "message" in error) messages.push(String((error as Error).message));
      if (typeof error === "string") messages.push(error);
      if (event.exception?.values?.[0]?.value) messages.push(event.exception.values[0].value);
      if (event.message) messages.push(event.message);
      if (messages.some((m) => sentryIgnoreList.some((ignore) => m.includes(ignore)))) return null;
      return event;
    },
  });
}

if (process.env.REACT_APP_MS_CLARITY && getConsent() === "yes") {
  try { clarity.init(process.env.REACT_APP_MS_CLARITY); clarity.consent(); } catch { /* blocked */ }
}

export function identify(user: { id: string; email?: string | null; username?: string }) {
  Sentry.setUser({ id: user.id, email: user.email ?? undefined, username: user.username });
  try {
    if (clarity.hasStarted()) {
      clarity.setTag("environment", process.env.REACT_APP_NODE_ENV ?? "development");
      clarity.identify(user.id, { email: user.email ?? undefined, username: user.username });
    }
  } catch { /* ignore */ }
}
